import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { authService } from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};

// Hook personalizado para manejar la persistencia del token
const useTokenPersistence = () => {
  const getStoredToken = useCallback(() => {
    return localStorage.getItem("token");
  }, []);

  const getStoredUser = useCallback(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error("Error parseando usuario guardado:", error);
        localStorage.removeItem("user");
        return null;
      }
    }
    return null;
  }, []);

  const setStoredToken = useCallback((token) => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
  }, []);

  const setStoredUser = useCallback((user) => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  }, []);

  const clearStorage = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }, []);

  return {
    getStoredToken,
    getStoredUser,
    setStoredToken,
    setStoredUser,
    clearStorage,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const {
    getStoredToken,
    getStoredUser,
    setStoredToken,
    setStoredUser,
    clearStorage,
  } = useTokenPersistence();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = getStoredToken();
        const savedUser = getStoredUser();

        if (token) {
          // Si hay token, intentar obtener el usuario actual
          try {
            const response = await authService.getCurrentUser();
            const currentUser = response.data;
            setUser(currentUser);
            setStoredUser(currentUser); // Actualizar usuario guardado
          } catch (error) {
            console.log("Error obteniendo usuario actual:", error);

            // Solo eliminar token si es un error de autenticación (401)
            if (error.response?.status === 401) {
              clearStorage();
              setUser(null);
            } else {
              // Para otros errores (red, servidor, etc.), usar el usuario guardado
              if (savedUser) {
                setUser(savedUser);
              }
            }
          }
        } else if (savedUser) {
          // Si no hay token pero hay usuario guardado, limpiar
          clearStorage();
        }
      } catch (error) {
        console.error("Error inicializando autenticación:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [getStoredToken, getStoredUser, setStoredUser, clearStorage]);

  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      const { token, user } = response.data;

      setStoredToken(token);
      setStoredUser(user);
      setUser(user);

      return user;
    } catch (error) {
      console.error("Error en login:", error);
      throw error;
    }
  };

  const logout = () => {
    clearStorage();
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.ROL === "admin",
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
